import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Upload, X, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useCoachingTemplate } from '@/hooks/useCoachingTemplates';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { uploadAndParseDocument } from '@/lib/documentParser';
import { fetchTenantScopedCompanies, resolveTenantId } from '@/lib/tenantScope';

// Sample system prompt for reference
const SAMPLE_SYSTEM_PROMPT = `You are a post-session documentation agent. Using only the session transcript, metadata (rep, customer, objective, date), and any provided reference docs, generate a clean summary document that matches the uploaded output template exactly (same headings, order, and format—no extra sections). Extract the call plan or canvas content from what was actually discussed, flag gaps as "Not captured in session" instead of guessing, and ensure compliance by correcting any risky or unsupported claims. Keep everything short and practical (bullets/checklists). Include the rep's draft wording and the improved coach-approved version where relevant, and output only the completed document content ready to drop into the template.`;

type CoachingTemplateCategory =
  | 'sales'
  | 'strategy'
  | 'people'
  | 'communication'
  | 'education'
  | 'professional_dev'
  | 'professional_development';

function normaliseTemplateCategory(category: CoachingTemplateCategory): string {
  return category === 'professional_development' || category === 'professional_dev' ? 'education' : category;
}

function deriveTemplateIdentifiers(templateName: string) {
  const trimmedName = templateName.trim();
  const templateType = trimmedName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || 'custom_template';

  return {
    templateType,
    outputDocument: trimmedName,
  };
}

export default function EditCoachingTemplate() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, hasRole } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const { hasEnterpriseHierarchy } = useOrganisation();
  const currentTenantId = resolveTenantId(tenant, user?.tenant_id);
  const queryClient = useQueryClient();
  const { data: template, isLoading } = useCoachingTemplate(id);
  const [saving, setSaving] = useState(false);
  const [assignTo, setAssignTo] = useState<'all' | 'specific_companies'>('all');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [originalCompanyId, setOriginalCompanyId] = useState<string | null>(null);
  const [designTemplates, setDesignTemplates] = useState<Array<{ name: string; url: string; type?: string; extracted_text?: string; word_count?: number }>>([]);
  const [instructionDocs, setInstructionDocs] = useState<Array<{ name: string; url: string; type?: string; extracted_text?: string; word_count?: number }>>([]);
  const designTemplateInputRef = useRef<HTMLInputElement>(null);
  const instructionDocInputRef = useRef<HTMLInputElement>(null);

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-list', currentTenantId, isMasterTenant],
    queryFn: () => fetchTenantScopedCompanies({
      tenantId: currentTenantId,
      isMasterTenant,
      select: 'id, name',
    }),
    enabled: hasRole('admin') && (isMasterTenant || !!currentTenantId)
  });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'sales' as CoachingTemplateCategory,
    template_type: '',
    estimated_duration: 20,
    output_document: '',
    system_prompt: '',
    document_format: 'docx'
  });

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description || '',
        category: normaliseTemplateCategory(template.category as CoachingTemplateCategory) as CoachingTemplateCategory,
        template_type: template.template_type,
        estimated_duration: template.estimated_duration,
        output_document: template.output_document,
        system_prompt: template.system_prompt || '',
        document_format: template.document_format || 'docx'
      });
      // Load access control settings
      setAssignTo((template as any).assign_to === 'all' ? 'all' : 'specific_companies');
      setSelectedCompanyId((template as any).company_id || null);
      setOriginalCompanyId((template as any).company_id || null);
      // Load sample documents — split by type
      const docs = (template as any).sample_documents || [];
      setDesignTemplates(docs.filter((d: any) => d.type === 'design' || !d.type));
      setInstructionDocs(docs.filter((d: any) => d.type === 'instructions'));
    }
  }, [template]);

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'design' | 'instructions',
    setter: React.Dispatch<React.SetStateAction<Array<{ name: string; url: string; type?: string; extracted_text?: string; word_count?: number }>>>,
    currentList: Array<{ name: string; url: string }>,
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => {
    const files = Array.from(e.target.files || []);
    const maxFiles = type === 'design' ? 1 : 5;

    if (currentList.length + files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed`);
      return;
    }

    for (const file of files) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 20MB limit`);
        continue;
      }

      try {
        toast.loading(`Uploading ${file.name}...`, { id: `upload-${type}-${file.name}` });
        const folder = type === 'design' ? 'coaching-templates/design' : 'coaching-templates/instructions';
        const result = await uploadAndParseDocument(file, 'knowledge-sources', folder);

        if (!result.success) {
          toast.error(`Failed: ${result.error}`, { id: `upload-${type}-${file.name}` });
          continue;
        }

        const doc = {
          name: result.name,
          url: result.url,
          type,
          extracted_text: result.extracted_text,
          word_count: result.word_count,
        };

        if (type === 'design') {
          setter([doc]);
        } else {
          setter(prev => [...prev, doc]);
        }

        toast.success(`${file.name} uploaded`, { id: `upload-${type}-${file.name}` });
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`, { id: `upload-${type}-${file.name}` });
      }
    }

    if (inputRef.current) inputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !formData.name.trim()) return;
    const { templateType, outputDocument } = deriveTemplateIdentifiers(formData.name);
    
    setSaving(true);
    try {
      // Company admins preserve original company_id, super admins can change
      const isAdmin = hasRole('admin');
      const finalCompanyId = isAdmin 
        ? (assignTo === 'specific_companies' ? selectedCompanyId : null)
        : originalCompanyId;
      const finalAssignTo = isAdmin ? assignTo : 'specific_companies';

      const { error } = await supabase
        .from('coaching_templates')
        .update({
          name: formData.name,
          description: formData.description,
          category: normaliseTemplateCategory(formData.category),
          template_type: templateType,
          estimated_duration: formData.estimated_duration,
          output_document: outputDocument,
          system_prompt: formData.system_prompt,
          document_format: formData.document_format,
          assign_to: finalAssignTo,
          company_id: finalCompanyId,
          sample_documents: [
            ...designTemplates.map(d => ({ ...d, type: 'design' })),
            ...instructionDocs.map(d => ({ ...d, type: 'instructions' })),
          ]
        })
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Template updated successfully');
      queryClient.invalidateQueries({ queryKey: ['coaching-templates'] });
      navigate('/admin/coaching-templates');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <Button variant="ghost" onClick={() => navigate('/admin/coaching-templates')} className="mb-4 -ml-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Output Templates
          </Button>
          <h1 className="text-3xl font-bold text-foreground">Edit Output Template</h1>
          <p className="text-muted-foreground mt-1">Update this document output template</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Template Details */}
          <Card>
            <CardHeader>
              <CardTitle>Template Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Account Management Coach"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Develop strategic account plans for your key accounts"
                  rows={2}
                />
              </div>

              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v: any) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">📈 Sales</SelectItem>
                    <SelectItem value="strategy">🎯 Strategy</SelectItem>
                    <SelectItem value="people">👥 People</SelectItem>
                    <SelectItem value="communication">💬 Communication</SelectItem>
                    <SelectItem value="education">🎓 Education / Professional Development</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <p className="text-xs text-muted-foreground">
                The internal template key and generated document title are created automatically from the Template Name.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Estimated Duration (min)</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={formData.estimated_duration}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimated_duration: parseInt(e.target.value) || 20 }))}
                  />
                </div>

                <div>
                  <Label>Document Format</Label>
                  <Select value={formData.document_format} onValueChange={(v) => setFormData(prev => ({ ...prev, document_format: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="docx">📄 Word (.docx)</SelectItem>
                      <SelectItem value="pdf">📑 PDF</SelectItem>
                      <SelectItem value="markdown">📝 Markdown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

            </CardContent>
          </Card>

          {/* Design Template */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Design Template
              </CardTitle>
              <CardDescription>
                Upload the exact document you want the AI to replicate. The output will match this document's
                structure, headings, sections, and format as closely as possible.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="border-2 border-dashed border-primary/30 rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer bg-primary/5"
                onClick={() => designTemplateInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto text-primary/60 mb-2" />
                <p className="text-sm font-medium text-foreground">
                  Upload your design template (PDF, DOCX, TXT)
                </p>
                <p className="text-xs text-muted-foreground mt-1">1 file, 20MB max — the AI will replicate this exact format</p>
                <input
                  ref={designTemplateInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={(e) => handleFileUpload(e, 'design', setDesignTemplates, designTemplates, designTemplateInputRef)}
                  className="hidden"
                />
              </div>

              {designTemplates.length > 0 && (
                <div className="space-y-2">
                  {designTemplates.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">{doc.name}</span>
                        {doc.word_count ? <span className="text-xs text-muted-foreground">({doc.word_count} words)</span> : null}
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setDesignTemplates([])}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Instructions Document */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Instructions Document (Optional)
              </CardTitle>
              <CardDescription>
                Upload documents explaining HOW the output should be written — tone, content guidelines,
                what to include or exclude. These guide the AI's behaviour, not the visual layout.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => instructionDocInputRef.current?.click()}
              >
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Upload instruction documents (PDF, DOCX, TXT)</p>
                <p className="text-xs text-muted-foreground mt-1">Max 5 files, 20MB each</p>
                <input
                  ref={instructionDocInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  multiple
                  onChange={(e) => handleFileUpload(e, 'instructions', setInstructionDocs, instructionDocs, instructionDocInputRef)}
                  className="hidden"
                />
              </div>

              {instructionDocs.length > 0 && (
                <div className="space-y-2">
                  {instructionDocs.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{doc.name}</span>
                        {doc.word_count ? <span className="text-xs text-muted-foreground">({doc.word_count} words)</span> : null}
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setInstructionDocs(prev => prev.filter((_, i) => i !== index))}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* System Prompt */}
              <div>
                <Label htmlFor="system_prompt">Additional Instructions (Optional)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Type additional instructions or use the sample prompt below
                </p>
                <Textarea
                  id="system_prompt"
                  value={formData.system_prompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, system_prompt: e.target.value }))}
                  placeholder={SAMPLE_SYSTEM_PROMPT}
                  rows={6}
                  className="font-mono text-sm"
                />
                {!formData.system_prompt && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="mt-2 h-auto p-0 text-xs"
                    onClick={() => setFormData(prev => ({ ...prev, system_prompt: SAMPLE_SYSTEM_PROMPT }))}
                  >
                    Use sample prompt
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Visibility & Access */}
          <Card>
            <CardHeader>
              <CardTitle>Visibility & Access</CardTitle>
              <CardDescription>Control who can see and use this template</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Access Control</Label>
                <Select value={assignTo} onValueChange={(v: any) => setAssignTo(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">🌐 {hasEnterpriseHierarchy ? 'All Branches' : 'All Companies'}</SelectItem>
                    {hasRole('admin') && (
                      <SelectItem value="specific_companies">🏢 {hasEnterpriseHierarchy ? 'Specific Branch' : 'Specific Company'}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {assignTo === 'specific_companies' && hasRole('admin') && (
                <div>
                  <Label>{hasEnterpriseHierarchy ? 'Branch' : 'Company'}</Label>
                  <Select value={selectedCompanyId || ''} onValueChange={setSelectedCompanyId}>
                    <SelectTrigger>
                      <SelectValue placeholder={hasEnterpriseHierarchy ? 'Select a branch...' : 'Select a company...'} />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/admin/coaching-templates')} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
